import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiCheckCircle } from 'react-icons/fi';
import ApiURL from '../Api/Api';

const SuperAdminIncidents = () => {
  const [incidents, setIncidents] = useState([]);
  const [accountRequests, setAccountRequests] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const openCount = useMemo(() => incidents.filter((item) => item.status === 'open').length, [incidents]);

  const loadIncidents = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${ApiURL}/superadmin_get_chatbox.php`, { credentials: 'include' });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to load incidents');
      }
      setIncidents(data.incidents || []);
      setAccountRequests(data.account_requests || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load incidents');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadIncidents();
  }, []);

  const markFixed = async (incident) => {
    try {
      const response = await fetch(`${ApiURL}/superadmin_close_incident.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ incident_id: incident.id }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to mark fixed');
      }
      toast.success('Incident marked fixed');
      loadIncidents();
    } catch (error) {
      toast.error(error.message || 'Failed to mark fixed');
    }
  };

  if (isLoading) {
    return <div className="py-20 text-center text-sm text-slate-500">Loading incident reports...</div>;
  }

  return (
    <div className="mt-8 space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-xl font-semibold text-slate-950">Incident Report</h3>
            <p className="mt-1 text-sm text-slate-500">{openCount} active incident{openCount === 1 ? '' : 's'} need action.</p>
          </div>
          <button
            type="button"
            onClick={loadIncidents}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>

        <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Incident Type</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Opened</th>
                <th className="px-4 py-3">Last Message</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {incidents.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">No incidents found.</td>
                </tr>
              )}
              {incidents.map((incident) => {
                const messages = incident.messages || [];
                const lastMessage = messages[messages.length - 1];
                const isOpen = incident.status === 'open';
                return (
                  <React.Fragment key={incident.id}>
                    <tr className="align-top">
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase ${isOpen ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {isOpen ? 'Active' : 'Fixed'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-900">{incident.incident_type}</td>
                      <td className="px-4 py-3 text-slate-600">{incident.company_name || `Company #${incident.company_id}`}</td>
                      <td className="px-4 py-3 text-slate-600">{incident.owner_name || `Owner #${incident.company_owner_id}`}</td>
                      <td className="px-4 py-3 text-slate-500">{incident.opened_at}</td>
                      <td className="max-w-xs px-4 py-3 text-slate-600">
                        <div className="line-clamp-2">{lastMessage?.message || '-'}</div>
                        {messages.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setExpandedId(expandedId === incident.id ? null : incident.id)}
                            className="mt-2 text-xs font-bold text-cyan-700 hover:text-cyan-900"
                          >
                            {expandedId === incident.id ? 'Hide Conversation' : `View Conversation (${messages.length})`}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isOpen ? (
                          <button
                            type="button"
                            onClick={() => markFixed(incident)}
                            className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-50"
                          >
                            <FiCheckCircle /> Mark Fixed
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">Fixed {incident.closed_at || ''}</span>
                        )}
                      </td>
                    </tr>
                    {expandedId === incident.id && (
                      <tr>
                        <td colSpan="7" className="bg-slate-50 px-4 py-4">
                          <div className="space-y-2">
                            {messages.map((message) => (
                              <div key={message.id} className="rounded-lg bg-white p-3 shadow-sm">
                                <div className="flex flex-wrap justify-between gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
                                  <span>{message.sender}</span>
                                  <span>{message.created_at}</span>
                                </div>
                                <p className="mt-1 text-sm leading-6 text-slate-700">{message.message}</p>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-xl font-semibold text-slate-950">Company Account Requests</h3>
        <p className="mt-1 text-sm text-slate-500">Plan upgrade, renewal, email change, detail change, and ownership transfer requests.</p>
        <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Current</th>
                <th className="px-4 py-3">Requested</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {accountRequests.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">No account requests found.</td>
                </tr>
              )}
              {accountRequests.map((request) => (
                <tr key={request.id} className="align-top">
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase text-amber-800">{request.status}</span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{String(request.request_type || '').replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 text-slate-600">{request.owner_name || `Owner #${request.company_owner_id}`}</td>
                  <td className="max-w-xs px-4 py-3 text-slate-600">{request.current_value || '-'}</td>
                  <td className="max-w-sm px-4 py-3 text-slate-600">{request.requested_value}</td>
                  <td className="max-w-sm px-4 py-3 text-slate-600">{request.notes || '-'}</td>
                  <td className="px-4 py-3 text-slate-500">{request.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default SuperAdminIncidents;
